<template>
  <div class="user-profile">
    <h2 class="profile-header">사용자 정보</h2>

    <!-- 프로필 이미지 미리보기 및 업로드 -->

    <div class="profile-image-container">
      <label for="file-upload" class="profile-image-label">
        <img v-if="(previewImage || userInfo.profile_image_url) && !profileImageError"
          :src="previewImage || userInfo.profile_image_url"
          alt="프로필 이미지"
          class="profile-image"
          @error="profileImageError = true" />
        <span v-else class="no-profile">이미지 없음</span>
      </label>
      <input type="file" @change="handleFileUpload" accept="image/*" />
      
      <div class="upload-controls">
        <span v-if="selectedFile">{{ selectedFile.name }}</span>
        <button @click="updateProfileImage" :disabled="!selectedFile" class="upload-btn">이미지 변경</button>
      </div>
    </div>
    <!-- 사용자 정보 테이블 -->

    <table class="profile-table">
      <tbody>
        <tr>
          <th>ID</th>
          <td>{{ userInfo.username || "Not Assigned" }}</td>
        </tr>
        <tr>
          <th>이름</th>
          <td>{{ userInfo.last_name + " " + userInfo.first_name || "Not Assigned" }}</td>
        </tr>
        <tr>
          <th>부서</th>
          <td>{{ userInfo.department || "Not Assigned" }}</td>
        </tr>
        <tr>
          <th>권한그룹</th>
          <td>
            {{ userInfo.groups?.length > 0 ? userInfo.groups.join(", ") : "No Groups Assigned" }}
          </td>
        </tr>
        <tr>
          <th>Email</th>
          <td>{{ userInfo.email || "Not Provided" }}</td>
        </tr>
      </tbody>
    </table>

    <!-- 🎂 생일 입력 -->
    <div class="birthday-section" style="margin-top: 24px; max-width: 480px;">
      <MyBirthdayInput />
    </div>
  </div>
</template>

<script setup>
import MyBirthdayInput from '@/components/birthday/MyBirthdayInput.vue'
import axiosInstance from "@/api/axios"; // Axios 인스턴스 가져오기
import { ref, onMounted } from "vue";

const userInfo = ref({
  isAuthenticated: false,
  username: "",
  last_name: "",
  first_name: "",
  department: "",
  groups: [],
  email: "",
  profile_image_url: "",
}); // 사용자 정보 초기화

const selectedFile = ref(null);
const previewImage = ref(null); // 이미지 미리보기 URL
const profileImageError = ref(false);

const fetchUserProfile = async () => {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) throw new Error("No token found");

    const response = await axiosInstance.get("user/me/", {
      headers: { Authorization: `Bearer ${token}` },
    });
    
    if (response.data) {
      userInfo.value.isAuthenticated = true;
      userInfo.value = response.data;
    } else {
      throw new Error("Invalid user profile data");
    }
  } catch (error) {
    console.error("Failed to fetch user profile:", error);
  }
};

const handleFileUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    selectedFile.value = file;

    // 이미지 미리보기 생성
    const reader = new FileReader();
    reader.onload = (e) => {
      previewImage.value = e.target.result;
    };
    reader.readAsDataURL(file);
  }
};

const updateProfileImage = async () => {
    if (!selectedFile.value) {
      alert("이미지를 선택하세요.");
      return;
    }

    const formData = new FormData();
    formData.append("profile_image", selectedFile.value);

    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("No token found");

      const response = await axiosInstance.patch("user/update-profile/", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.profile_image_url) {
        userInfo.value.profile_image_url = response.data.profile_image_url; // UI 업데이트
        alert("프로필 이미지가 변경되었습니다.");
      }
    } catch (error) {
      console.error("Failed to update profile image:", error);
      alert("이미지 업로드에 실패했습니다.");
    }
};

onMounted(async () => {
  try {
    await fetchUserProfile();
  } catch (error) {
    console.error("Failed to fetch user profile:", error);
  }
});

</script>

<style scoped>
/* 프로필 전체 컨테이너 */
.user-profile {
  max-width: 500px;
  margin: 20px auto;
  text-align: center;
  background: #f9f9f9;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}
/* 프로필 제목 */
.profile-header {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
}

/* 프로필 이미지 컨테이너 */
.profile-image-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 15px;
}


/* 프로필 이미지 */
.profile-image {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #ccc;
  transition: transform 0.3s ease-in-out;
}
/* 프로필 이미지 미리보기 효과 */
.profile-image:hover {
  transform: scale(1.05);
}
/* 파일 업로드 라벨 */
.profile-image-label {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* 이미지 없을 때 */
.no-profile {
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #ddd;
  font-size: 14px;
  color: #555;
}

/* 업로드 컨트롤 */
.upload-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 10px;
}

/* 업로드 버튼 */
.upload-btn {
  padding: 8px 12px;
  background: #007bff;
  color: #fff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 5px;
}

.upload-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* 테이블 스타일 */
.profile-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.profile-table th,
.profile-table td {
  padding: 10px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.profile-table th {
  background: #f1f1f1;
  width: 30%;
  font-weight: bold;
}

.profile-table td {
  width: 70%;
}
</style>
